/**
 * The DOM side of the runtime: builds the shadow tree from the scene, applies data, measures layout (at load and
 * update only, never per frame) and writes evaluated frames as inline styles, touching only what changed.
 */
import type { AnimatableProperty, Element, Field, Fill, GroupElement, Scene, Shadow, ShapeStyle } from '@prism-graphics/scene-schema';
import { timelinePhases } from '@prism-graphics/scene-schema/layouts';
import type { Bound, Box } from './engine/resolve.ts';
import type { FrameState, PropMap, PropValue, SplitBy } from './engine/evaluate.ts';
import { isColor } from './engine/color.ts';

export interface Snapshot {
  /** Auto-size boxes (element id → measured outer size). */
  boxes: Record<string, Box>;
  /** Shrink-to-fit text sizes (element id → font size in px). */
  fontSizes: Record<string, number>;
  /** Line-split text: the line index of each word. */
  lines: Record<string, number[]>;
  /** Layout boxes (offset in the parent, size) of field-bound text and images, for crossfade ghosts. */
  rects: Record<string, { x: number; y: number; w: number; h: number }>;
  /** Nested elements anchored to the canvas: their left, right, top and bottom (CSS) under this layout. */
  anchors?: Record<string, [string, string, string, string]>;
  /** Wrapping split text: the words wider than the box, which may break inside. */
  breaks?: Record<string, number[]>;
  /** Split text with a gradient fill: the text box and each part's offset in it (x, y pairs). */
  grads?: Record<string, { w: number; h: number; at: number[] }>;
}

export type Data = Record<string, unknown>;

/** A data version as the renderer needs it for crossfades: the data and its measured layout. */
export type VersionView = (version: number) => { data: Data; snapshot: Snapshot } | undefined;

const segmenter = typeof Intl === 'object' && Intl.Segmenter ? new Intl.Segmenter('en', { granularity: 'grapheme' }) : undefined;
/** User-perceived characters (grapheme clusters), so å written as a + ring stays one character. */
const graphemes = (s: string): string[] => (segmenter ? Array.from(segmenter.segment(s), (x) => x.segment) : Array.from(s));

function transformCss(x: number, y: number, r: number, sx: number, sy: number, prefix = ''): string {
  let tf = prefix;
  if (x !== 0 || y !== 0) tf += `translate(${px(x)},${px(y)}) `;
  if (r !== 0) tf += `rotate(${round(r)}deg) `;
  if (sx !== 1 || sy !== 1) tf += `scale(${round(sx)},${round(sy)})`;
  return tf.trim() || 'none';
}

interface Node {
  el: Element;
  node: HTMLElement;
  img?: HTMLImageElement;
  inStack: boolean;
  /** Static CSS translate for centre/middle anchors. */
  anchorShift: string;
  autoW: boolean;
  autoH: boolean;
  /** Values set by data bindings that feed base values. */
  boundColor?: string;
  boundOpacity?: number;
  /** Last written inline style values. */
  cache: Map<string, string>;
  /** Split text (staggered tracks): 'word' (word spans) or 'char' (character spans inside word spans). */
  split?: 'word' | 'char' | undefined;
  lineSplit?: boolean;
  textValue?: string;
  words?: HTMLElement[];
  chars?: HTMLElement[];
  /** Crossfade: the old content, a sibling just below the element. */
  ghost?: HTMLElement | undefined;
  ghostFrom?: number;
  video?: HTMLVideoElement;
  canvas?: HTMLCanvasElement;
  /** The time (µs) whose frame the canvas shows, and the frame being fetched (ADR 0029). */
  shown?: number;
  pending?: { t: number; done?: Promise<string | undefined> } | undefined;
  playing?: boolean;
  /** Clip mask with fixed content: the wrapper that counters the mask's animated offset. */
  inner?: HTMLElement;
  /** Nested and anchored to the canvas (positioned at measure time). */
  toCanvas?: boolean;
  parent?: Node | undefined;
  /** Text with a gradient fill (the CSS gradient). */
  grad?: string;
}

const partCache = new WeakMap<HTMLElement, Map<string, string>>();

const px = (n: number) => `${round(n)}px`;
const CLIP_TEXT = 'background-clip:text;-webkit-background-clip:text;-webkit-text-fill-color:transparent;';
function round(n: number): number {
  return Math.round(n * 1e4) / 1e4;
}

/** Only hex colours (validated) and the engine's own rgb()/rgba() output reach CSS. */
function colorCss(c: string): string {
  return isColor(c) || /^rgba?\([0-9.,]+\)$/.test(c) ? c : 'transparent';
}

function fillCss(fill: Fill | undefined): { color: string; image: string } {
  if (!fill) return { color: 'transparent', image: 'none' };
  if (fill.type === 'solid') return { color: colorCss(fill.color), image: 'none' };
  const stops = fill.stops.map((s) => `${colorCss(s.color)} ${round(s.offset * 100)}%`).join(',');
  return { color: 'transparent', image: `linear-gradient(${round(fill.angle)}deg,${stops})` };
}

function shadowCss(s: Shadow | undefined): string {
  if (!s) return 'none';
  return `${px(s.x)} ${px(s.y)} ${px(s.blur)} ${colorCss(s.color)}`;
}

function radiusCss(r: ShapeStyle['cornerRadius']): string {
  if (r === undefined) return '0';
  if (typeof r === 'number') return px(r);
  return r.map(px).join(' ');
}

export class Renderer {
  readonly root: HTMLDivElement;
  readonly nodes = new Map<string, Node>();
  readonly #scene: Scene;
  readonly #families: Map<string, string>;
  readonly #imageUrl: (hash: string) => string;
  readonly #fieldsById: Map<string, Field>;
  /** Package path → asset hash, for image-choice values. */
  readonly #pathToHash: Map<string, string>;
  #snapshot: Snapshot = { boxes: {}, fontSizes: {}, lines: {}, rects: {} };
  #visible = '';
  /** Text elements with staggered tracks and how finely they split. */
  readonly #splits = new Map<string, Set<SplitBy>>();
  /** Elements bound to a field by text or image (crossfade candidates). */
  readonly #fadeable = new Set<string>();
  /** Character-split text that keeps its kerning (measured pair adjustments). */
  readonly #kern = new Set<string>();
  #ctx: CanvasRenderingContext2D | null | undefined;
  readonly #kerns = new Map<string, number>();
  /** goToTime: video frames still being fetched (each resolves with an error message, or undefined once shown). */
  seeks: Promise<string | undefined>[] = [];

  constructor(shadow: ShadowRoot, scene: Scene, families: Map<string, string>, imageUrl: (hash: string) => string, assetPaths: Record<string, string>) {
    this.#scene = scene;
    this.#families = families;
    this.#imageUrl = imageUrl;
    this.#fieldsById = new Map(scene.fields.map((f) => [f.id, f]));
    this.#pathToHash = new Map(Object.entries(assetPaths).map(([h, p]) => [p, h]));
    for (const [, ph] of timelinePhases(scene.timeline)) {
      for (const tr of ph.tracks) {
        if (!tr.stagger) continue;
        let set = this.#splits.get(tr.element);
        if (!set) this.#splits.set(tr.element, (set = new Set()));
        set.add(tr.stagger.by);
        if (tr.stagger.by === 'char' && tr.stagger.kerning) this.#kern.add(tr.element);
      }
    }
    for (const f of scene.fields) for (const b of f.bindings) if (b.property === 'text' || b.property === 'image') this.#fadeable.add(b.element);
    const style = document.createElement('style');
    style.textContent =
      ':host{display:block;position:absolute;left:0;top:0;width:100%;height:100%;overflow:hidden;pointer-events:none;contain:strict}' +
      '.r,.r *{box-sizing:border-box;margin:0}' +
      // Every node is its own compositing layer from the start, so a frame's pixels never depend on which
      // frames were rendered before it (opacity crossing 1 would otherwise create or drop a layer).
      '.r [data-pg-id],.r [data-pg-ghost],.r .w,.r .c{will-change:opacity}' +
      '.w,.c{display:inline-block;white-space:pre}' +
      '.r{position:absolute;left:0;top:0;transform-origin:0 0;visibility:hidden}' +
      'img,video{display:block;user-select:none}';
    this.root = document.createElement('div');
    this.root.className = 'r';
    this.root.style.width = px(scene.canvas.width);
    this.root.style.height = px(scene.canvas.height);
    for (const el of scene.elements) this.root.append(this.#build(el, undefined));
    shadow.replaceChildren(style, this.root);
  }

  /**
   * Scale the canvas by `s` and centre it in a `w`×`h` frame: a frame of another aspect (a host that renders a layout
   * at a size of a different shape) gets equal margins; a frame of the canvas's aspect gets no offset.
   */
  setScale(s: number, w = this.#scene.canvas.width * s, h = this.#scene.canvas.height * s): void {
    const x = round((w - this.#scene.canvas.width * s) / 2);
    const y = round((h - this.#scene.canvas.height * s) / 2);
    this.root.style.transform = (x || y ? `translate(${px(x)},${px(y)})` : '') + (s === 1 ? '' : `scale(${round(s)})`);
  }

  /** All <img> elements, for decode() at load. */
  images(): HTMLImageElement[] {
    return [...this.nodes.values()].flatMap((n) => (n.img ? [n.img] : []));
  }

  /** All <video> elements (load waits until each can play). */
  videos(): HTMLVideoElement[] {
    return [...this.nodes.values()].flatMap((n) => (n.video ? [n.video] : []));
  }

  #build(el: Element, parent: Element | undefined): HTMLElement {
    const node = document.createElement(el.type === 'image' ? 'img' : 'div');
    node.setAttribute('data-pg-id', el.id);
    const s = node.style;
    const inStack = parent !== undefined && (parent.type === 'group' && parent.layout.mode === 'stack');
    const autoW = el.size.width === 'auto' && (el.type === 'group' || el.type === 'clipMask');
    const autoH = el.size.height === 'auto' && (el.type === 'group' || el.type === 'clipMask');
    let anchorShift = '';
    let toCanvas = false;
    if (inStack) {
      s.position = 'relative';
      s.flex = '0 1 auto';
      s.minWidth = '0';
    } else {
      s.position = 'absolute';
      const a = el.anchor ?? { horizontal: 'left', vertical: 'top', to: 'parent' };
      const hx: string[] = [];
      if (a.horizontal === 'left' || a.horizontal === 'stretch') s.left = '0';
      if (a.horizontal === 'right' || a.horizontal === 'stretch') s.right = '0';
      if (a.horizontal === 'center') {
        s.left = '50%';
        hx.push('-50%');
      } else hx.push('0');
      if (a.vertical === 'top' || a.vertical === 'stretch') s.top = '0';
      if (a.vertical === 'bottom' || a.vertical === 'stretch') s.bottom = '0';
      if (a.vertical === 'middle') {
        s.top = '50%';
        hx.push('-50%');
      } else hx.push('0');
      if (hx[0] !== '0' || hx[1] !== '0') anchorShift = `translate(${hx[0]},${hx[1]}) `;
      toCanvas = a.to === 'canvas' && parent !== undefined;
    }
    if (el.blendMode && el.blendMode !== 'normal') s.mixBlendMode = el.blendMode;
    const t = el.transform;
    s.transformOrigin = `${round(t.originX * 100)}% ${round(t.originY * 100)}%`;
    if (!el.visible) s.display = 'none';
    const z = el.size;
    if (z.minWidth !== undefined) s.minWidth = px(z.minWidth);
    if (z.maxWidth !== undefined) s.maxWidth = px(z.maxWidth);
    else if (el.type === 'text' && (inStack || (parent && parent.size.width !== 'auto'))) s.maxWidth = '100%';
    if (z.minHeight !== undefined) s.minHeight = px(z.minHeight);
    if (z.maxHeight !== undefined) s.maxHeight = px(z.maxHeight);

    const n: Node = { el, node, inStack, anchorShift, autoW, autoH, cache: new Map(), toCanvas, parent: parent && this.nodes.get(parent.id) };
    const splits = this.#splits.get(el.id);
    if (splits && el.type === 'text') {
      n.split = splits.has('char') ? 'char' : 'word';
      n.lineSplit = splits.has('line');
    }
    this.nodes.set(el.id, n);

    switch (el.type) {
      case 'group':
      case 'clipMask': {
        let box: HTMLElement = node;
        if (el.type === 'group') this.#groupStyle(el, s);
        else {
          s.overflow = 'hidden';
          if (el.shape === 'ellipse') s.borderRadius = '50%';
          else if (el.cornerRadius !== undefined) s.borderRadius = radiusCss(el.cornerRadius);
          if (el.fixedContent) {
            box = n.inner = document.createElement('div');
            box.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%';
            node.append(box);
          }
        }
        for (const c of el.children) box.append(this.#build(c, el));
        break;
      }
      case 'text': {
        const ts = el.style;
        s.fontFamily = `"${this.#families.get(ts.font) ?? 'sans-serif'}",sans-serif`;
        s.fontWeight = String(ts.fontWeight);
        s.fontStyle = ts.fontStyle;
        s.fontSize = px(ts.fontSize);
        s.lineHeight = String(ts.lineHeight);
        s.textTransform = ts.textTransform;
        s.textAlign = ts.textAlign;
        s.fontKerning = 'normal';
        if (ts.fill?.type === 'linear-gradient') {
          n.grad = fillCss(ts.fill).image;
          // Split text paints the gradient on each part (aligned at measure time); whole text on itself.
          if (!n.split) s.cssText += `${CLIP_TEXT}background-image:${n.grad}`;
        }
        if (ts.stroke) s.setProperty('-webkit-text-stroke', `${px(ts.stroke.width)} ${colorCss(ts.stroke.color)}`);
        if (ts.shadow) s.textShadow = shadowCss(ts.shadow);
        const o = el.overflow;
        if (o.mode === 'wrap') {
          s.whiteSpace = 'pre-wrap';
          s.overflowWrap = 'break-word';
          s.overflow = 'hidden';
          s.display = el.visible ? '-webkit-box' : 'none';
          s.setProperty('-webkit-box-orient', 'vertical');
          s.setProperty('-webkit-line-clamp', String(o.maxLines));
        } else {
          s.whiteSpace = 'pre';
          if (o.mode !== 'none') {
            s.overflow = 'hidden';
            s.textOverflow = 'ellipsis';
          }
        }
        this.#text(n, el.text);
        break;
      }
      case 'rect':
      case 'ellipse': {
        const st = el.style as ShapeStyle;
        const f = fillCss(st.fill);
        s.backgroundImage = f.image;
        s.borderRadius = el.type === 'ellipse' ? '50%' : radiusCss(st.cornerRadius);
        if (st.stroke) s.border = `${px(st.stroke.width)} solid ${colorCss(st.stroke.color)}`;
        if (st.shadow) s.boxShadow = shadowCss(st.shadow);
        break;
      }
      case 'line': {
        s.height = px(el.stroke.width);
        s.backgroundColor = colorCss(el.stroke.color);
        if (el.cap === 'round') s.borderRadius = px(el.stroke.width / 2);
        break;
      }
      case 'image': {
        const img = node as HTMLImageElement;
        img.alt = '';
        img.decoding = 'sync';
        img.draggable = false;
        s.objectFit = el.fit;
        img.src = this.#imageUrl(el.asset);
        n.img = img;
        break;
      }
      case 'video': {
        // Real time shows the <video> itself; otherwise each seeked frame is drawn into a canvas, so a frame on
        // screen never depends on when the compositor picks up the video's new frame.
        const v = (n.video = document.createElement('video'));
        const c = (n.canvas = document.createElement('canvas'));
        v.muted = v.defaultMuted = v.playsInline = true;
        v.preload = 'auto';
        v.loop = el.loop;
        for (const e of [v, c]) e.style.cssText = `position:absolute;left:0;top:0;width:100%;height:100%;object-fit:${el.fit}`;
        v.style.display = 'none';
        v.addEventListener('loadeddata', () => void showFrame(n, v.currentTime));
        const url = this.#imageUrl(el.asset);
        if (url && !url.startsWith('data:image/')) v.src = url;
        node.append(v, c);
        break;
      }
    }
    this.#staticSize(n);
    return node;
  }

  #groupStyle(el: GroupElement, s: CSSStyleDeclaration): void {
    const L = el.layout;
    if (L.mode === 'stack') {
      const st = L.stack ?? { direction: 'vertical', gap: 0, align: 'start', justify: 'start' };
      s.display = el.visible ? 'flex' : 'none';
      s.flexDirection = st.direction === 'horizontal' ? 'row' : 'column';
      s.gap = px(st.gap);
      s.alignItems = { start: 'flex-start', center: 'center', end: 'flex-end', stretch: 'stretch' }[st.align];
      s.justifyContent = { start: 'flex-start', center: 'center', end: 'flex-end', 'space-between': 'space-between' }[st.justify];
    }
    if (L.padding) s.padding = `${px(L.padding.top)} ${px(L.padding.right)} ${px(L.padding.bottom)} ${px(L.padding.left)}`;
    const f = fillCss(el.style.fill);
    s.backgroundImage = f.image;
    s.borderRadius = radiusCss(el.style.cornerRadius);
    if (el.style.stroke) s.border = `${px(el.style.stroke.width)} solid ${colorCss(el.style.stroke.color)}`;
    if (el.style.shadow) s.boxShadow = shadowCss(el.style.shadow);
    if (el.clipContent) s.overflow = 'hidden';
  }

  /** The designed width/height in CSS, used at build and before measuring. */
  #staticSize(n: Node): void {
    const z = n.el.size;
    const s = n.node.style;
    s.width = typeof z.width === 'number' ? px(z.width) : n.autoW ? 'fit-content' : '';
    s.height = typeof z.height === 'number' ? px(z.height) : '';
    n.cache.delete('width');
    n.cache.delete('height');
  }

  // -------------------------------------------------------------------------------------------------------------
  // Data

  /** Apply data (already normalised to the fields' types) to the DOM. Values are inserted as text only. */
  applyData(data: Data): void {
    for (const f of this.#scene.fields) {
      const v = data[f.id];
      for (const b of f.bindings) {
        const n = this.nodes.get(b.element);
        if (!n) continue;
        switch (b.property) {
          case 'text':
            this.#text(n, typeof v === 'string' ? v : v === undefined || v === null ? '' : String(v));
            break;
          case 'color':
            n.boundColor = isColor(v) ? v : undefined;
            break;
          case 'strokeColor':
            if (isColor(v)) {
              if (n.el.type === 'text') n.node.style.setProperty('-webkit-text-stroke-color', v);
              else n.node.style.borderColor = v;
            }
            break;
          case 'visible': {
            const show = v !== false && n.el.visible;
            const display = n.el.type === 'group' && n.el.layout.mode === 'stack' ? 'flex' : n.el.type === 'text' && n.el.overflow.mode === 'wrap' ? '-webkit-box' : '';
            n.node.style.display = show ? display : 'none';
            break;
          }
          case 'image': {
            const hash = typeof v === 'string' ? (this.#pathToHash.get(v) ?? v) : undefined;
            if (n.img && hash && (f.type !== 'image-choice' || f.choices.includes(hash))) {
              const url = this.#imageUrl(hash);
              if (n.img.getAttribute('src') !== url) n.img.src = url;
            }
            break;
          }
          case 'opacity':
            n.boundOpacity = typeof v === 'number' && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : undefined;
            break;
        }
      }
    }
  }

  get fields(): Map<string, Field> {
    return this.#fieldsById;
  }

  /** Set a text element's content; split text becomes word (and character) spans, inserted as text only. */
  #text(n: Node, s: string): void {
    if (!n.split) {
      n.node.textContent = s;
      return;
    }
    if (n.textValue === s && n.words) return;
    n.textValue = s;
    const words: HTMLElement[] = [];
    const chars: HTMLElement[] = [];
    const span = (cls: string, text?: string) => {
      const e = document.createElement('span');
      e.className = cls;
      if (text !== undefined) e.textContent = text;
      return e;
    };
    const parts: Array<HTMLElement | string> = [];
    for (const tok of s.split(/(\s+)/)) {
      if (!tok) continue;
      if (/^\s/.test(tok)) {
        parts.push(tok);
        continue;
      }
      const w = span('w', n.split === 'word' ? tok : undefined);
      if (n.split === 'char') {
        const gs = graphemes(tok);
        gs.forEach((g, i) => {
          const c = span('c', g);
          // Each character is its own box, which loses the font's pair kerning: put it back (in em, so it follows
          // shrink-to-fit).
          const k = i + 1 < gs.length && this.#kern.has(n.el.id) ? this.#kerning(n, g, gs[i + 1]!) : 0;
          if (k) c.style.marginRight = `${round(k)}em`;
          w.append(chars[chars.push(c) - 1]!);
        });
      }
      words.push(w);
      parts.push(w);
    }
    n.node.replaceChildren(...parts);
    n.words = words;
    n.chars = chars;
  }

  /** The kerning between two characters of a text element's font, in em (measured once per pair). */
  #kerning(n: Node, a: string, b: string): number {
    if (n.el.type !== 'text') return 0;
    const ts = n.el.style;
    const tf = ts.textTransform;
    const cased = (c: string) => (tf === 'uppercase' ? c.toUpperCase() : tf === 'lowercase' ? c.toLowerCase() : c);
    const font = `${ts.fontStyle} ${ts.fontWeight} 100px "${this.#families.get(ts.font) ?? 'sans-serif'}"`;
    const key = `${font}|${a}|${b}`;
    let k = this.#kerns.get(key);
    if (k === undefined) {
      const ctx = this.#ctx === undefined ? (this.#ctx = document.createElement('canvas').getContext('2d')) : this.#ctx;
      k = 0;
      if (ctx) {
        ctx.font = font;
        ctx.fontKerning = 'normal';
        const w = (t: string) => ctx.measureText(t).width;
        k = (w(cased(a) + cased(b)) - w(cased(a)) - w(cased(b))) / 100;
      }
      this.#kerns.set(key, k);
    }
    return Math.abs(k) < 1e-4 ? 0 : k;
  }

  /** The number of parts a split text element has under the data currently shown. */
  count = (id: string, by: SplitBy): number => {
    const n = this.nodes.get(id);
    if (!n?.words) return 0;
    if (by === 'char') return n.chars!.length;
    if (by === 'word') return n.words.length;
    const lines = this.#snapshot.lines[id];
    return lines?.length ? lines[lines.length - 1]! + 1 : n.words.length ? 1 : 0;
  };

  /** Bound colours and opacities under `data` (for crossfades), as applyData would set them. */
  bound(data: Data): Bound {
    const out: Record<string, { color?: string; opacity?: number }> = {};
    for (const f of this.#scene.fields) {
      const v = data[f.id];
      for (const b of f.bindings) {
        const o = (out[b.element] ??= {});
        if (b.property === 'color' && isColor(v)) o.color = v;
        if (b.property === 'opacity' && typeof v === 'number' && Number.isFinite(v)) o.opacity = Math.min(1, Math.max(0, v));
      }
    }
    return out;
  }

  // -------------------------------------------------------------------------------------------------------------
  // Layout measurement (load and update only)

  /** Measure the current data's layout: shrink-to-fit text sizes, then auto-size box sizes. */
  measure(): Snapshot {
    for (const n of this.nodes.values()) {
      this.#staticSize(n);
      const s = n.node.style;
      s.transform = '';
      n.cache.delete('transform');
      if (n.el.type === 'text') {
        s.letterSpacing = px(n.el.style.letterSpacing);
        s.fontSize = px(n.el.style.fontSize);
        n.cache.delete('letter-spacing');
      }
    }
    // Layout px per CSS px of the canvas (root scale and any host transform), for fractional measurements.
    const rootRect = this.root.getBoundingClientRect();
    const k = this.root.offsetWidth > 0 && rootRect.width > 0 ? rootRect.width / this.root.offsetWidth : 1;
    const width = (e: HTMLElement) => e.getBoundingClientRect().width / k;
    const fontSizes: Record<string, number> = {};
    for (const n of this.nodes.values()) {
      const el = n.el;
      if (el.type !== 'text' || el.overflow.mode !== 'shrink') continue;
      const min = el.overflow.minFontSize;
      const s = n.node.style;
      let size = el.style.fontSize;
      for (let i = 0; i < 6 && size > min; i++) {
        const avail = width(n.node);
        const saved = [s.maxWidth, s.width, s.flexShrink];
        s.maxWidth = 'none';
        s.width = 'max-content';
        s.flexShrink = '0';
        const natural = width(n.node);
        [s.maxWidth, s.width, s.flexShrink] = saved as [string, string, string];
        if (natural <= avail + 0.01 || avail <= 0) break;
        size = Math.max(min, Math.floor(size * (avail / natural) * 0.995 * 100) / 100);
        s.fontSize = px(size);
      }
      fontSizes[el.id] = size;
    }
    const boxes: Record<string, Box> = {};
    for (const n of this.nodes.values()) {
      if (!n.autoW && !n.autoH) continue;
      const r = n.node.getBoundingClientRect();
      // Round up to a quarter pixel so the box never ends up a fraction narrower than its content.
      boxes[n.el.id] = { w: Math.ceil((r.width / k) * 4) / 4, h: Math.ceil((r.height / k) * 4) / 4 };
    }
    // Nested elements anchored to the canvas: offset by where their parent's padding box sits (layout only; the
    // transforms are cleared while measuring). Parents come first, so nested ones see their final place.
    const anchors: NonNullable<Snapshot['anchors']> = {};
    const W = this.#scene.canvas.width;
    const H = this.#scene.canvas.height;
    for (const n of this.nodes.values()) {
      if (!n.toCanvas) continue;
      const p = n.node.parentElement!;
      const r = p.getBoundingClientRect();
      // The ancestors' designed translations (and centring shifts) count: they place the parent on the canvas.
      let ox = (r.left - rootRect.left) / k + p.clientLeft;
      let oy = (r.top - rootRect.top) / k + p.clientTop;
      for (let q = n.parent; q; q = q.parent) {
        ox += q.el.transform.x - (q.el.anchor?.horizontal === 'center' && !q.inStack ? q.node.offsetWidth / 2 : 0);
        oy += q.el.transform.y - (q.el.anchor?.vertical === 'middle' && !q.inStack ? q.node.offsetHeight / 2 : 0);
      }
      const a = n.el.anchor!;
      const h = a.horizontal;
      const v = a.vertical;
      const right = px(ox + p.clientWidth - W);
      const bottom = px(oy + p.clientHeight - H);
      const v4: [string, string, string, string] = [
        h === 'center' ? px(W / 2 - ox) : h === 'right' ? '' : px(-ox),
        h === 'right' || h === 'stretch' ? right : '',
        v === 'middle' ? px(H / 2 - oy) : v === 'bottom' ? '' : px(-oy),
        v === 'bottom' || v === 'stretch' ? bottom : '',
      ];
      anchors[n.el.id] = v4;
      this.#anchor(n, v4);
    }
    // Wrapping split text: a word wider than the box may break inside (otherwise it would overflow).
    const breaks: NonNullable<Snapshot['breaks']> = {};
    for (const n of this.nodes.values()) {
      if (!n.words || n.el.type !== 'text' || n.el.overflow.mode !== 'wrap') continue;
      const avail = n.node.clientWidth;
      const long = n.words.flatMap((w, i) => (w.offsetWidth > avail + 0.5 ? [i] : []));
      if (long.length) {
        breaks[n.el.id] = long;
        this.#breakWords(n, long);
      }
    }
    const lines: Record<string, number[]> = {};
    const rects: Snapshot['rects'] = {};
    const grads: NonNullable<Snapshot['grads']> = {};
    for (const n of this.nodes.values()) {
      const e = n.node;
      if (n.grad && n.words) {
        const parts = n.split === 'char' ? n.chars! : n.words;
        grads[n.el.id] = { w: e.clientWidth, h: e.clientHeight, at: parts.flatMap((c) => [c.offsetLeft, c.offsetTop]) };
      }
      if (this.#fadeable.has(n.el.id)) rects[n.el.id] = { x: e.offsetLeft, y: e.offsetTop, w: e.offsetWidth, h: e.offsetHeight };
      if (!n.lineSplit || !n.words) continue;
      let top = -Infinity;
      let line = -1;
      lines[n.el.id] = n.words.map((w) => {
        if (w.offsetTop > top + 1) {
          line++;
          top = w.offsetTop;
        }
        return line;
      });
    }
    const out: Snapshot = { boxes, fontSizes, lines, rects };
    if (Object.keys(anchors).length) out.anchors = anchors;
    if (Object.keys(breaks).length) out.breaks = breaks;
    if (Object.keys(grads).length) {
      out.grads = grads;
      for (const n of this.nodes.values()) this.#gradParts(n, grads[n.el.id]);
    }
    return out;
  }

  #anchor(n: Node, v: [string, string, string, string]): void {
    const s = n.node.style;
    [s.left, s.right, s.top, s.bottom] = v;
  }

  #breakWords(n: Node, which: readonly number[]): void {
    for (const i of which) {
      const w = n.words?.[i];
      if (w) w.style.cssText += 'white-space:normal;overflow-wrap:anywhere';
    }
  }

  /** Split text with a gradient: each part shows its slice of one gradient laid over the whole text box. */
  #gradParts(n: Node, g: { w: number; h: number; at: number[] } | undefined): void {
    if (!g || !n.grad || !n.words) return;
    const parts = n.split === 'char' ? n.chars! : n.words;
    parts.forEach((c, i) => {
      c.style.cssText += `${CLIP_TEXT}background-image:${n.grad};background-size:${px(g.w)} ${px(g.h)};background-position:${px(-(g.at[2 * i] ?? 0))} ${px(-(g.at[2 * i + 1] ?? 0))}`;
    });
  }

  /** Make a measured snapshot current (font sizes now, box sizes as base values for the next frames). */
  applySnapshot(s: Snapshot): void {
    this.#snapshot = s;
    for (const n of this.nodes.values()) {
      if (n.el.type === 'text') n.node.style.fontSize = px(s.fontSizes[n.el.id] ?? n.el.style.fontSize);
      const a = s.anchors?.[n.el.id];
      if (a) this.#anchor(n, a);
      const b = s.breaks?.[n.el.id];
      if (b) this.#breakWords(n, b);
      this.#gradParts(n, s.grads?.[n.el.id]);
    }
  }

  /**
   * Videos at a frame: `local` is the time (ms) since the graphic became visible, undefined while it is not.
   * Real time plays them in step with the clock; otherwise they are paused and seeked, and the seeks still in
   * progress go to `seeks` (goToTime waits for their frames).
   */
  media(local: number | undefined, play: boolean): void {
    this.seeks = [];
    for (const n of this.nodes.values()) {
      const v = n.video;
      if (!v || n.el.type !== 'video') continue;
      if (n.playing !== play) {
        n.playing = play;
        v.style.display = play ? '' : 'none';
        n.canvas!.style.display = play ? 'none' : '';
      }
      const d = v.duration;
      // A non-finite time (never expected) must not reach currentTime: assigning NaN throws.
      let target = local !== undefined && Number.isFinite(local) ? Math.max(0, local) / 1000 : 0;
      if (Number.isFinite(d) && d > 0) target = n.el.loop ? target % d : Math.min(target, d);
      if (play) {
        if (local === undefined) {
          if (!v.paused) v.pause();
        } else if (v.paused) {
          if (!v.ended || n.el.loop) {
            v.currentTime = target;
            void v.play().catch(() => undefined);
          }
        } else {
          const off = Math.abs(v.currentTime - target);
          if ((Number.isFinite(d) ? Math.min(off, d - off) : off) > 0.25) v.currentTime = target;
        }
        n.shown = n.pending = undefined;
        continue;
      }
      if (!v.paused) v.pause();
      const same = Math.abs(v.currentTime - target) < 1e-4 && !v.seeking;
      if (same && n.shown === us(target)) continue;
      if (!same) v.currentTime = target;
      const p = showFrame(n, target);
      if (local !== undefined) this.seeks.push(p);
    }
  }

  // -------------------------------------------------------------------------------------------------------------
  // Frames

  /** The designed value of a property under the current data and layout snapshot. */
  base = (id: string, prop: AnimatableProperty): PropValue | undefined => {
    const n = this.nodes.get(id);
    if (!n) return undefined;
    const el = n.el;
    const t = el.transform;
    switch (prop) {
      case 'x':
        return t.x;
      case 'y':
        return t.y;
      case 'scaleX':
        return t.scaleX;
      case 'scaleY':
        return t.scaleY;
      case 'rotation':
        return t.rotation;
      case 'opacity':
        return n.boundOpacity ?? t.opacity;
      case 'width': {
        const b = this.#snapshot.boxes[id];
        if (n.autoW && b) return b.w;
        return typeof el.size.width === 'number' ? el.size.width : undefined;
      }
      case 'height': {
        const b = this.#snapshot.boxes[id];
        if (n.autoH && b) return b.h;
        return typeof el.size.height === 'number' ? el.size.height : undefined;
      }
      case 'insetTop':
      case 'insetRight':
      case 'insetBottom':
      case 'insetLeft':
        return 0;
      case 'blur':
        return 'style' in el && el.type !== 'text' ? ((el.style as ShapeStyle).blur ?? 0) : 0;
      case 'letterSpacing':
        return el.type === 'text' ? el.style.letterSpacing : 0;
      case 'color': {
        if (n.boundColor) return n.boundColor;
        if (el.type === 'text') return el.style.color;
        if (el.type === 'rect' || el.type === 'ellipse' || el.type === 'group') {
          const f = (el.style as ShapeStyle).fill;
          return f?.type === 'solid' ? f.color : undefined;
        }
        return undefined;
      }
    }
  };

  #set(n: Node, prop: string, value: string): void {
    if (n.cache.get(prop) === value) return;
    n.cache.set(prop, value);
    n.node.style.setProperty(prop, value);
  }

  /** Write a frame. `ver` gives older data versions, for crossfade ghosts. */
  write(frame: FrameState, ver?: VersionView): void {
    const vis = frame.visible ? 'visible' : 'hidden';
    if (vis !== this.#visible) {
      this.#visible = vis;
      this.root.style.visibility = vis;
    }
    for (const n of this.nodes.values()) {
      const id = n.el.id;
      const m: PropMap = frame.values.get(id) ?? {};
      const get = (p: AnimatableProperty): PropValue | undefined => (m[p] !== undefined ? m[p] : this.base(id, p));
      const num = (p: AnimatableProperty, d: number): number => {
        const v = get(p);
        return typeof v === 'number' && Number.isFinite(v) ? v : d;
      };
      this.#set(n, 'transform', transformCss(num('x', 0), num('y', 0), num('rotation', 0), num('scaleX', 1), num('scaleY', 1), n.anchorShift));
      if (n.inner) {
        const tf = transformCss(n.el.transform.x - num('x', 0), n.el.transform.y - num('y', 0), 0, 1, 1);
        if (n.inner.style.transform !== tf) n.inner.style.transform = tf;
      }
      const opacity = Math.min(1, Math.max(0, num('opacity', 1)));
      const fade = frame.fades.get(id);
      this.#set(n, 'opacity', String(round(opacity * (fade ? fade.p : 1))));
      const w = get('width');
      const h = get('height');
      if (typeof w === 'number') this.#set(n, 'width', px(Math.max(0, w)));
      if (typeof h === 'number') this.#set(n, 'height', px(Math.max(0, h)));
      const it = num('insetTop', 0);
      const ir = num('insetRight', 0);
      const ib = num('insetBottom', 0);
      const il = num('insetLeft', 0);
      this.#set(n, 'clip-path', it || ir || ib || il ? `inset(${round(it)}% ${round(ir)}% ${round(ib)}% ${round(il)}%)` : 'none');
      const blur = num('blur', 0);
      this.#set(n, 'filter', blur > 0 ? `blur(${px(blur)})` : 'none');
      const c = get('color');
      if (typeof c === 'string') this.#set(n, n.el.type === 'text' ? 'color' : 'background-color', colorCss(c));
      if (n.el.type === 'text') this.#set(n, 'letter-spacing', px(num('letterSpacing', 0)));
      if (n.words) this.#parts(n, frame.parts.get(id));
      const v = fade && ver?.(fade.from);
      if (fade && v) this.#ghost(n, fade.from, v, opacity * (1 - fade.p));
      else if (n.ghost) {
        n.ghost.remove();
        n.ghost = undefined;
      }
    }
  }

  /** Staggered values on the word and character spans; lines move their words together. */
  #parts(n: Node, p: Partial<Record<SplitBy, PropMap[]>> | undefined): void {
    const lineOf = this.#snapshot.lines[n.el.id];
    n.words!.forEach((w, i) => partStyle(w, { ...p?.line?.[lineOf?.[i] ?? 0], ...p?.word?.[i] }));
    n.chars!.forEach((c, i) => partStyle(c, p?.char?.[i]));
  }

  /** The crossfade's outgoing layer: a copy of the element showing data version `from`, at its old layout box. */
  #ghost(n: Node, from: number, v: { data: Data; snapshot: Snapshot }, opacity: number): void {
    const id = n.el.id;
    let g = n.ghost;
    if (!g || n.ghostFrom !== from) {
      g?.remove();
      g = n.node.cloneNode(false) as HTMLElement;
      g.removeAttribute('data-pg-id');
      g.setAttribute('data-pg-ghost', id);
      const r = v.snapshot.rects[id] ?? { x: n.node.offsetLeft, y: n.node.offsetTop, w: n.node.offsetWidth, h: n.node.offsetHeight };
      const s = g.style;
      s.position = 'absolute';
      s.left = px(r.x);
      s.top = px(r.y);
      s.right = s.bottom = 'auto';
      s.width = px(r.w);
      s.height = px(r.h);
      s.margin = '0';
      s.minWidth = s.maxWidth = s.minHeight = s.maxHeight = 'none';
      s.flex = 'none';
      if (n.el.type === 'text') {
        const size = v.snapshot.fontSizes[id];
        if (size !== undefined) s.fontSize = px(size);
      }
      let text: string | undefined;
      let image: string | undefined;
      for (const f of this.#scene.fields) {
        for (const b of f.bindings) {
          if (b.element !== id) continue;
          const val = v.data[f.id];
          if (b.property === 'text') text = typeof val === 'string' ? val : val == null ? '' : String(val);
          if (b.property === 'image' && typeof val === 'string') image = this.#pathToHash.get(val) ?? val;
        }
      }
      if (text !== undefined) g.textContent = text;
      if (n.grad && n.split) s.cssText += `${CLIP_TEXT}background-image:${n.grad}`;
      if (image !== undefined && g instanceof HTMLImageElement) g.src = this.#imageUrl(image);
      n.node.before(g);
      n.ghost = g;
      n.ghostFrom = from;
    }
    // Every value write() animates is copied each frame (not only taken from the clone), so the ghost at t is a
    // pure function of t, never of which frame of the fade happened to be rendered first.
    const s = g.style;
    const ns = n.node.style;
    s.transform = ns.transform;
    s.clipPath = ns.clipPath;
    s.filter = ns.filter;
    s.color = ns.color;
    s.backgroundColor = ns.backgroundColor;
    s.letterSpacing = ns.letterSpacing;
    s.opacity = String(round(opacity));
  }

  /** Forget written values so the next write() rewrites everything (after measuring). */
  invalidate(): void {
    for (const n of this.nodes.values()) n.cache.clear();
  }
}

const us = (t: number) => Math.round(t * 1e6);

/**
 * Puts a paused video's frame at time `t` (s) into its canvas; resolves once it is there (undefined), or with why
 * not after 5 s. The video's current frame is checked, not trusted: after `seeked`, Chromium can still hand out the
 * frame of an earlier seek for a while, so the frame is taken as a VideoFrame and drawn only when its timestamp
 * shows it is the frame at `t` (or the last frame, or a frame at or before `t` that has stayed current for 250 ms:
 * no duration, gaps). Polled with setTimeout, so it needs no animation frames; every deadline (250 ms, the 1 s
 * re-seek, the 5 s limit) is a setTimeout too, never a clock reading, so it holds under a host that freezes
 * performance.now and Date (PRISM's graphics host; ADR 0029).
 */
export function showFrame(n: Pick<Node, 'video' | 'canvas' | 'shown' | 'pending'>, t: number): Promise<string | undefined> {
  const T = us(t);
  if (n.pending?.t === T) return n.pending.done!;
  const v = n.video!;
  const c = n.canvas!;
  const p: NonNullable<Node['pending']> = (n.pending = { t: T });
  return (p.done = new Promise<string | undefined>((res) => {
    const vf = typeof VideoFrame == 'function';
    let last = NaN;
    // Deadline flags, set by timers: the current frame has stayed current 250 ms; the re-seek is due.
    let held = false;
    let due = false;
    let poller: ReturnType<typeof setTimeout> | undefined;
    let hold: ReturnType<typeof setTimeout> | undefined;
    const timers = [setTimeout(() => (due = true), 1e3), setTimeout(() => resolve(`video frame at ${t} s not decoded within 5 s`), 5e3)];
    const resolve = (e?: string) => {
      for (const x of [poller, hold, ...timers]) clearTimeout(x);
      if (n.pending === p) n.pending = undefined;
      res(e);
    };
    const poll = () => {
      // Superseded, or no source (yet: `loadeddata` fetches the frame then).
      if (n.pending !== p || !v.currentSrc) return resolve();
      if (v.error) return resolve(`video ${v.currentSrc} failed`);
      if (!v.seeking && v.readyState >= 2) {
        let f: VideoFrame | undefined;
        try {
          f = vf ? new VideoFrame(v) : undefined;
        } catch {}
        const ts = f ? f.timestamp : 0;
        const fresh = ts !== last;
        if (fresh) {
          last = ts;
          held = false;
          clearTimeout(hold);
          hold = setTimeout(() => (held = true), 250);
        }
        const end = f ? ts + (f.duration ?? 0) : 0;
        // Without WebCodecs: the current frame, a poll after the seek settled.
        if (f ? ts <= T + 1 && (T < end || end >= v.duration * 1e6 - 1e3 || held) : !vf && !fresh) {
          c.width = v.videoWidth;
          c.height = v.videoHeight;
          const ctx = c.getContext('2d')!;
          ctx.clearRect(0, 0, c.width, c.height);
          ctx.drawImage(f ?? v, 0, 0, c.width, c.height);
          f?.close();
          n.shown = T;
          return resolve();
        }
        f?.close();
      }
      // A seek that never delivers its frame is issued once more.
      if (due && !v.seeking) (due = false), (v.currentTime = t);
      poller = setTimeout(poll, 4);
    };
    poll();
  }));
}

/** Write a part's (character, word or line) values; parts have no designed transform, so rest is identity. */
function partStyle(e: HTMLElement, m: PropMap | undefined): void {
  let cache = partCache.get(e);
  if (!cache) partCache.set(e, (cache = new Map()));
  const num = (p: AnimatableProperty, d: number): number => {
    const v = m?.[p];
    return typeof v === 'number' && Number.isFinite(v) ? v : d;
  };
  const blur = num('blur', 0);
  const c = m?.color;
  const set = (prop: string, value: string) => {
    if (cache.get(prop) === value) return;
    cache.set(prop, value);
    e.style.setProperty(prop, value);
  };
  set('transform', transformCss(num('x', 0), num('y', 0), num('rotation', 0), num('scaleX', 1), num('scaleY', 1)));
  set('opacity', String(round(Math.min(1, Math.max(0, num('opacity', 1))))));
  set('filter', blur > 0 ? `blur(${px(blur)})` : 'none');
  set('color', typeof c === 'string' ? colorCss(c) : '');
}
